import { useEffect, useState } from "react";

import { cn } from "@voicerun/design-system";

import { Button } from ".";
import { Icon } from "./Icon";
import { Spinner } from "../base";

interface PlayButtonProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  audioElement?: HTMLAudioElement | null;
  className?: string;
  audioUrl?: string;
  isLoading?: boolean;
  size?: PlayButtonSize;
}

const BUTTON_SIZE_CONFIG = {
  sm: {
    buttonClass: "!w-7 !h-7",
    iconSize: 14,
    spinnerSize: 12,
  },
  md: {
    buttonClass: "!w-8 !h-8",
    iconSize: 18,
    spinnerSize: 16,
  },
  lg: {
    buttonClass: "!w-10 !h-10",
    iconSize: 22,
    spinnerSize: 18,
  },
  xl: {
    buttonClass: "!w-12 !h-12",
    iconSize: 26,
    spinnerSize: 22,
  },
} as const;

type PlayButtonSize = keyof typeof BUTTON_SIZE_CONFIG;

export const PlayButton = ({ isPlaying, onPlayPause, audioElement, className = "", audioUrl, isLoading = false, size = "lg" }: PlayButtonProps) => {
  const [progress, setProgress] = useState(0);

  const { buttonClass, iconSize, spinnerSize } = BUTTON_SIZE_CONFIG[size] ?? BUTTON_SIZE_CONFIG.md;

  // Compare URLs by creating URL objects to handle different formats
  const isCurrentAudio =
    audioUrl && audioElement?.src
      ? new URL(audioElement.src).pathname.endsWith(new URL(audioUrl).pathname) || new URL(audioElement.src).pathname === new URL(audioUrl).pathname
      : false;

  useEffect(() => {
    if (!audioElement) return;

    const handleTimeUpdate = () => {
      if (!isCurrentAudio) return;
      if (!audioElement.duration || audioElement.duration === Infinity) return;
      const currentProgress = (audioElement.currentTime / audioElement.duration) * 100;
      setProgress(Math.min(currentProgress, 100));
    };

    audioElement.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      audioElement.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioElement, isCurrentAudio]);

  // Reset progress when playback stops
  useEffect(() => {
    if (!isPlaying) {
      // TODO
      // setProgress(0);
    }
  }, [isPlaying]);

  const circumference = 2 * Math.PI * 11; // radius of 11 for a 24px circle

  return (
    <Button
      onClick={onPlayPause}
      type="button"
      variant="icon"
      size="icon"
      className={cn("!p-0 !rounded-full relative", buttonClass, className)}
      isDisabled={isLoading}
      aria-busy={isLoading}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-white/80 dark:bg-gray-900/70">
          <Spinner
            size={spinnerSize}
            variant="brand"
          />
        </div>
      )}
      <svg
        className="absolute inset-0 -rotate-90 w-full h-full"
        viewBox="0 0 24 24"
      >
        <circle
          cx="12"
          cy="12"
          r="11"
          strokeWidth="2"
          fill="none"
          className="stroke-gray-200 dark:stroke-gray-700"
        />
        <circle
          cx="12"
          cy="12"
          r="11"
          strokeWidth="2"
          fill="none"
          className="stroke-brand dark:stroke-brand-light transition-all duration-200"
          strokeDasharray={circumference}
          strokeDashoffset={isCurrentAudio ? circumference - (progress / 100) * circumference : circumference}
          strokeLinecap="round"
        />
      </svg>
      <Icon
        name={isPlaying ? "pause" : "play"}
        size={iconSize}
        className="relative z-10"
      />
    </Button>
  );
};
